import { useState, useEffect, useCallback } from 'react';
import { idem } from '@/utils/api';
import { useSessionPresence } from '@/utils/presence';
import { useMenuDesign } from '@/contexts/ThemeContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, 
  Minus, 
  ShoppingCart, 
  Clock,
  X,
  Send,
  Bell,
  Users,
  ChevronRight,
  Loader2,
  QrCode,
  Receipt,
  UserPlus,
  User,
  RefreshCw,
  CheckCircle2,
  Search,
  Hand
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ModernCustomerMenu from '@/modern/customer/ModernCustomerMenu';
import { useIsModern } from '@/modern/shared';

// For custom domains, we need to call the actual backend URL
// If running on a custom domain without backend proxy, use the Emergent backend directly
const getApiUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  // If we're on a custom domain (not the preview domain), use the configured backend URL
  // This ensures API calls work even when frontend is served from a custom domain
  if (typeof window !== 'undefined') {
    const currentHost = window.location.hostname;
    const previewHost = backendUrl ? new URL(backendUrl).hostname : '';
    // If current host is different from the preview host, we're on a custom domain
    if (currentHost !== previewHost && currentHost !== 'localhost') {
      // Use the backend URL from env (the Emergent preview URL)
      return `${backendUrl}/api`;
    }
  }
  return `${backendUrl}/api`;
};

const API = getApiUrl();

export default function CustomerMenu() {
  const { qrCode } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const isModern = useIsModern();
  const [generalNote, setGeneralNote] = useState('');
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(null);
  const [business, setBusiness] = useState(null);
  useMenuDesign(business?.appearance?.menu_theme || 'classic');
  const [unit, setUnit] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [session, setSession] = useState(null);
  useSessionPresence(session?.session_token ? session.session_id : null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState(() => {
    // Recover cart from localStorage
    const stored = localStorage.getItem(`cart_${qrCode}`);
    return stored ? JSON.parse(stored) : [];
  });
  const [showCart, setShowCart] = useState(false);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productConfig, setProductConfig] = useState({
    quantity: 1,
    variant: null,
    extras: [],
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Check-in state
  const [participantId, setParticipantId] = useState(() => {
    // Recover participant from localStorage if exists
    const stored = localStorage.getItem(`participant_${qrCode}`);
    return stored ? JSON.parse(stored) : null;
  });
  const [participantName, setParticipantName] = useState('');
  const [showCheckInDialog, setShowCheckInDialog] = useState(false);
  const [isIndividualMode, setIsIndividualMode] = useState(false);
  
  // My orders state
  const [myOrders, setMyOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('menu');
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [callingWaiter, setCallingWaiter] = useState(false);
  const [waiterCalled, setWaiterCalled] = useState(false);
  const [billRequested, setBillRequested] = useState(false);
  
  // Split bill / Comensal state
  const [showJoinSplitDialog, setShowJoinSplitDialog] = useState(false);
  const [joinName, setJoinName] = useState('');
  const [joinPhone, setJoinPhone] = useState('');
  const [joiningAsComensal, setJoiningAsComensal] = useState(false);
  const [miComensal, setMiComensal] = useState(() => {
    const stored = localStorage.getItem(`comensal_${qrCode}`);
    return stored ? JSON.parse(stored) : null;
  });

  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    if (qrCode) {
      localStorage.setItem(`cart_${qrCode}`, JSON.stringify(cart));
    }
  }, [cart, qrCode]);

  // Persist session to localStorage
  useEffect(() => {
    if (session && qrCode) {
      localStorage.setItem(`session_${qrCode}`, JSON.stringify({
        session_id: session.session_id,
        unit_name: unit?.name,
        business_name: business?.name,
        timestamp: Date.now()
      }));
    }
  }, [session, qrCode, unit, business]);

  useEffect(() => {
    fetchMenu();
  }, [qrCode]);

  // Auto-refresh my orders every 15 seconds when on orders tab
  useEffect(() => {
    if (activeTab === 'orders' && session) {
      const interval = setInterval(fetchMyOrders, 15000);
      return () => clearInterval(interval);
    }
  }, [activeTab, session, participantId]);

  const fetchMenu = async () => {
    try {
      const response = await axios.get(`${API}/public/menu/${qrCode}`);
      if (response.data.unavailable) {
        setUnavailable(response.data.business);
        return;
      }
      setBusiness(response.data.business);
      setUnit(response.data.unit);
      setCategories(response.data.categories);
      setProducts(response.data.products);
      setSession(response.data.session);
      
      // Check consumption mode
      const consumptionMode = response.data.unit?.consumption_mode || 'shared';
      setIsIndividualMode(consumptionMode === 'individual');
      
      // If no session, start one
      if (!response.data.session && response.data.unit) {
        const sessionRes = await axios.post(`${API}/public/sessions/${qrCode}/start`);
        setSession(sessionRes.data);
      } else if (response.data.session) {
        // Mesa ya abierta (otro celular o QR renovado): /start devuelve la misma sesión con su token vigente
        const sessionRes = await axios.post(`${API}/public/sessions/${qrCode}/start`);
        setSession(sessionRes.data);
        // If session exists and mode is individual, check if we need to join
        if (consumptionMode === 'individual' && !participantId) {
          setShowCheckInDialog(true);
        }
        // Fetch existing orders
        fetchMyOrders();
      }
    } catch (error) {
      console.error('Error fetching menu:', error);
      if (error.response?.status === 404) {
        setError('QR no válido o mesa no encontrada');
      } else {
        setError('Error al cargar el menú. Intenta de nuevo.');
      }
      toast.error('Error al cargar el menú');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyOrders = useCallback(async () => {
    if (!session) return;
    
    try {
      setRefreshing(true);
      let url = `${API}/public/sessions/${session.session_id}/orders`;
      if (participantId) {
        url += `?participant_id=${participantId.id}`;
      }
      const response = await axios.get(url);
      setMyOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setRefreshing(false);
    }
  }, [session, participantId]);

  const handleCheckIn = async () => {
    if (!participantName.trim()) {
      toast.error('Ingresa tu nombre');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/public/sessions/${session.session_id}/join`, {
        name: participantName.trim()
      });
      
      const newParticipant = {
        id: response.data.participant_id,
        name: response.data.name
      };
      
      setParticipantId(newParticipant);
      localStorage.setItem(`participant_${qrCode}`, JSON.stringify(newParticipant));
      setShowCheckInDialog(false);
      toast.success(`¡Bienvenido ${newParticipant.name}!`);
      return newParticipant;
    } catch (error) {
      toast.error('Error al registrarte');
    }
  };

  // Join as comensal (for split bill)
  const handleJoinAsComensal = async () => {
    if (!joinName.trim()) {
      toast.error('Ingresa tu nombre');
      return;
    }
    
    setJoiningAsComensal(true);
    try {
      const response = await axios.post(`${API}/public/sessions/${session.session_id}/join-split`, {
        nombre: joinName.trim(),
        telefono: joinPhone.trim() || null
      });
      
      const newComensal = response.data.comensal;
      setMiComensal(newComensal);
      localStorage.setItem(`comensal_${qrCode}`, JSON.stringify(newComensal));
      setShowJoinSplitDialog(false);
      setJoinName('');
      setJoinPhone('');
      toast.success(`¡Listo ${newComensal.nombre}! Tu código es ${newComensal.codigo}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al unirte');
    } finally {
      setJoiningAsComensal(false);
    }
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setProductConfig({
      quantity: 1,
      variant: product.variants?.length > 0 ? product.variants[0].name : null,
      extras: [],
      notes: ''
    });
    setShowProductDialog(true);
  };

  const handleAddToCart = () => {
    const item = {
      product_id: selectedProduct.product_id,
      product: selectedProduct,
      quantity: productConfig.quantity,
      variant: productConfig.variant,
      extras: productConfig.extras,
      notes: productConfig.notes
    };
    
    setCart([...cart, item]);
    setShowProductDialog(false);
    toast.success('Agregado al carrito');
  };

  const handleRemoveFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  const calculateItemPrice = (item) => {
    let price = item.product.price;
    
    // Add variant price
    if (item.variant) {
      const variant = item.product.variants?.find(v => v.name === item.variant);
      if (variant) price += variant.price_modifier || 0;
    }
    
    // Add extras price
    item.extras.forEach(extraName => {
      const extra = item.product.extras?.find(e => e.name === extraName);
      if (extra) price += extra.price || 0;
    });
    
    return price * item.quantity;
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + calculateItemPrice(item), 0);
  };

  const handleSubmitOrder = async (override) => {
    if (cart.length === 0) return;
    const participant = override?.id ? override : participantId;
    
    // Check if individual mode requires check-in
    if (isIndividualMode && !participant) {
      setShowCheckInDialog(true);
      return;
    }
    
    setSubmitting(true);
    try {
      const orderData = {
        items: cart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          variant: item.variant,
          extras: item.extras,
          notes: item.notes
        })),
        participant_id: participant?.id || null,
        notes: generalNote.trim() || null
      };
      
      const orderRes = await axios.post(
        `${API}/public/sessions/${session.session_id}/orders`,
        orderData,
        idem()
      );
      
      setCart([]);
      setGeneralNote('');
      setShowCart(false);
      if (orderRes.status === 202) toast.info('Tu pedido espera la confirmación del mozo');
      else toast.success('¡Pedido enviado!');
      
      // Refresh session and orders
      fetchMenu();
      setActiveTab('orders');
    } catch (error) {
      console.error('Error submitting order:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar pedido');
    } finally {
      setSubmitting(false);
    }
  };

  const getOrderStatusColor = (status) => {
    const colors = {
      'Nuevo': 'bg-blue-100 text-blue-700',
      'Confirmado': 'bg-purple-100 text-purple-700',
      'En preparación': 'bg-orange-100 text-orange-700',
      'Listo': 'bg-green-100 text-green-700',
      'Entregado': 'bg-gray-100 text-gray-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const callWaiter = async () => {
    if (!session || callingWaiter || waiterCalled) return;
    
    setCallingWaiter(true);
    try {
      await axios.post(`${API}/public/sessions/${session.session_id}/call-waiter`);
      setWaiterCalled(true);
      toast.success('¡Mozo notificado! Llegará en breve.');
      
      // Reset after 2 minutes
      setTimeout(() => setWaiterCalled(false), 120000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al llamar al mozo');
    } finally {
      setCallingWaiter(false);
    }
  };

  const requestBill = async () => {
    if (!session || billRequested) return;
    try {
      await axios.post(`${API}/public/sessions/${session.session_id}/request-bill`);
      setBillRequested(true);
      toast.success('Le avisamos al mozo que querés la cuenta');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo pedir la cuenta');
    }
  };

  const calculateMyTotal = () => {
    return myOrders.reduce((sum, order) => sum + (order.total || 0), 0);
  };

  const filteredProducts = products.filter(p => {
    // Filter by category if selected
    const matchesCategory = !selectedCategory || p.category_id === selectedCategory;
    
    // Filter by search query
    const query = searchQuery.toLowerCase().trim();
    const matchesSearch = !query || 
      p.name.toLowerCase().includes(query) ||
      (p.description && p.description.toLowerCase().includes(query));
    
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando menú...</p>
        </div>
      </div>
    );
  }

  if (unavailable) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface p-6" data-testid="menu-unavailable">
        <div className="text-center max-w-sm">
          {unavailable.logo_url && <img src={unavailable.logo_url} alt={unavailable.name} className="h-24 mx-auto mb-6 object-contain" data-testid="menu-unavailable-logo" />}
          <h1 className="text-2xl font-bold text-brand mb-2">{unavailable.name}</h1>
          <p className="text-gray-600" data-testid="menu-unavailable-text">El menú está temporalmente no disponible.</p>
        </div>
      </div>
    );
  }

  if (!business || !unit || error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface p-4">
        <div className="text-center max-w-md">
          <QrCode className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h1 className="text-xl font-bold mb-2">
            {error ? 'Error' : 'QR no válido'}
          </h1>
          <p className="text-gray-600 mb-4">
            {error || 'Este código QR no está asociado a ninguna mesa'}
          </p>
          <Button 
            onClick={() => window.location.reload()}
            className="bg-brand"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  if (isModern) {
    return (
      <ModernCustomerMenu business={business} unit={unit} categories={categories} products={products} cart={cart} setCart={setCart}
        myOrders={myOrders} callWaiter={callWaiter} requestBill={requestBill} waiterCalled={waiterCalled} billRequested={billRequested}
        callingWaiter={callingWaiter} submitOrder={handleSubmitOrder} submitting={submitting} itemPrice={calculateItemPrice}
        cartTotal={calculateTotal()} generalNote={generalNote} setGeneralNote={setGeneralNote}
        needsCheckIn={isIndividualMode && !participantId} participantName={participantName} setParticipantName={setParticipantName} checkIn={handleCheckIn} />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Clean and Modern */}
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-bold text-xl text-gray-900">{business.name}</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="inline-flex items-center gap-1 text-sm font-medium text-brand">
                  <QrCode className="w-3 h-3" />
                  {unit.name}
                </span>
                {participantId && (
                  <Badge className="bg-orange-500 text-white text-xs px-2 py-0.5">
                    {participantId.name}
                  </Badge>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Call Waiter Button */}
              <Button 
                variant={waiterCalled ? "default" : "outline"}
                size="icon"
                onClick={callWaiter}
                disabled={callingWaiter || waiterCalled || !session}
                className={`h-11 w-11 rounded-full ${waiterCalled ? "bg-green-500 hover:bg-green-600" : "border-gray-200"}`}
                data-testid="call-waiter-btn"
                title="Llamar al mozo"
              >
                {callingWaiter ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : waiterCalled ? (
                  <CheckCircle2 className="w-5 h-5 text-white" />
                ) : (
                  <Hand className="w-5 h-5" />
                )}
              </Button>
              
              <Button
                variant={billRequested ? "default" : "outline"}
                size="icon"
                onClick={requestBill}
                disabled={billRequested || !session}
                className={`h-11 w-11 rounded-full ${billRequested ? "bg-green-500 hover:bg-green-600" : "border-gray-200"}`}
                data-testid="request-bill-btn"
                title="Pedir la cuenta"
              >
                <Receipt className="w-5 h-5" />
              </Button>

              {/* Cart Button */}
              <Button 
                size="icon"
                className="relative h-11 w-11 rounded-full bg-brand hover:bg-brand/90 text-brand-foreground"
                onClick={() => setShowCart(true)}
                data-testid="cart-btn"
              >
                <ShoppingCart className="w-5 h-5" />
                {cart.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-white">
                    {cart.reduce((acc, item) => acc + item.quantity, 0)}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="sticky top-[73px] z-40 bg-white shadow-sm">
          <TabsList className="w-full justify-start rounded-none h-14 bg-white p-0 border-b">
            <TabsTrigger 
              value="menu" 
              className="flex-1 h-full rounded-none border-b-2 border-transparent data-[state=active]:border-brand data-[state=active]:bg-transparent data-[state=active]:text-brand font-medium"
            >
              <QrCode className="w-4 h-4 mr-2" />
              Menú
            </TabsTrigger>
            <TabsTrigger 
              value="orders"
              className="flex-1 h-full rounded-none border-b-2 border-transparent data-[state=active]:border-brand data-[state=active]:bg-transparent data-[state=active]:text-brand font-medium relative"
            >
              <Receipt className="w-4 h-4 mr-2" />
              Mi Cuenta
              {myOrders.length > 0 && (
                <span className="ml-2 w-5 h-5 bg-brand text-brand-foreground text-xs rounded-full flex items-center justify-center">
                  {myOrders.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Menu Tab */}
        <TabsContent value="menu" className="mt-0">
          {/* Search Bar */}
          <div className="px-4 py-3 bg-white">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="¿Qué estás buscando?"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-11 h-12 bg-gray-100 border-0 rounded-xl focus:ring-2 focus:ring-brand placeholder:text-gray-400"
                data-testid="product-search-input"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full"
                  onClick={() => setSearchQuery('')}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Category Navigation - Horizontal Scroll */}
          <div className="px-4 py-3 bg-white border-b border-gray-100">
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              <button
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${
                  !selectedCategory 
                    ? 'bg-brand text-brand-foreground shadow-md shadow-brand/20' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                onClick={() => setSelectedCategory(null)}
              >
                🍴 Todo
              </button>
              {categories.map((category, idx) => {
                const emojis = ['🥗', '🍝', '🍕', '🍰', '🍺', '🥤', '🍔', '🍣'];
                return (
                  <button
                    key={category.category_id}
                    className={`px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${
                      selectedCategory === category.category_id
                        ? 'bg-brand text-brand-foreground shadow-md shadow-brand/20' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    onClick={() => setSelectedCategory(category.category_id)}
                  >
                    {emojis[idx % emojis.length]} {category.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Products List */}
          <div className="p-4 pb-28">
            {filteredProducts.length === 0 ? (
              <div className="text-center py-16">
                {searchQuery ? (
                  <>
                    <Search className="w-16 h-16 mx-auto text-gray-200 mb-4" />
                    <p className="text-gray-500 mb-2">No encontramos "{searchQuery}"</p>
                    <Button variant="outline" size="sm" onClick={() => setSearchQuery('')} className="rounded-full">
                      Limpiar búsqueda
                    </Button>
                  </>
                ) : (
                  <p className="text-gray-600">No hay productos en esta categoría</p>
                )}
              </div>
            ) : (
              <div className="grid gap-3">
                {filteredProducts.map((product) => (
                  <div 
                    key={product.product_id}
                    className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden cursor-pointer hover:shadow-md transition-all active:scale-[0.98]"
                    onClick={() => handleProductClick(product)}
                    data-testid={`product-${product.product_id}`}
                  >
                    <div className="flex p-3 gap-3">
                      {/* Product Image */}
                      {product.image_url ? (
                        <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
                          <img 
                            src={product.image_url} 
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-brand/10 to-brand/20 flex-shrink-0 flex items-center justify-center">
                          <span className="text-3xl">🍽️</span>
                        </div>
                      )}
                      
                      {/* Product Info */}
                      <div className="flex-1 min-w-0 flex flex-col justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900 text-base leading-tight">
                            {product.name}
                          </h3>
                          {product.description && (
                            <p className="text-sm text-gray-500 mt-0.5 line-clamp-2 leading-snug">
                              {product.description}
                            </p>
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-lg font-bold text-brand">
                            ${product.price.toLocaleString()}
                          </span>
                          {product.preparation_time > 0 && (
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {product.preparation_time} min
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Add Button */}
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-brand flex items-center justify-center text-brand-foreground shadow-md">
                          <Plus className="w-5 h-5" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Orders/Bill Tab */}
        <TabsContent value="orders" className="mt-0">
          <div className="p-4 pb-28">
            {/* Header with refresh */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-bold text-xl text-gray-900">
                {miComensal ? `${miComensal.nombre}` : participantId ? `Cuenta de ${participantId.name}` : 'Mi Cuenta'}
              </h2>
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchMyOrders}
                disabled={refreshing}
                className="rounded-full"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>

            {/* Join Split Bill Banner */}
            {!miComensal && session && (
              <div className="mb-4 p-4 bg-gradient-to-r from-violet-500 to-purple-600 rounded-2xl text-white">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg">¿Van a dividir la cuenta?</h3>
                    <p className="text-white/80 text-sm mb-3">
                      Registrate para que te asignen tu parte y recibir tu cuenta por WhatsApp
                    </p>
                    <Button 
                      onClick={() => setShowJoinSplitDialog(true)}
                      className="bg-white text-violet-600 hover:bg-white/90 rounded-full"
                      data-testid="join-split-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Unirme a la cuenta
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Comensal Card (if joined) */}
            {miComensal && (
              <div className="mb-4 p-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                      <User className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">{miComensal.nombre}</h3>
                      <p className="text-white/80 text-sm">Código: {miComensal.codigo}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">${(miComensal.subtotal || 0).toLocaleString()}</p>
                    <Badge className={miComensal.pagado ? "bg-white text-green-600" : "bg-white/20 text-white"}>
                      {miComensal.pagado ? 'Pagado' : 'Pendiente'}
                    </Badge>
                  </div>
                </div>
                {miComensal.items?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/20">
                    <p className="text-sm text-white/80 mb-2">Tus items:</p>
                    {miComensal.items.map((item, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span>{item.nombre} {item.porcentaje < 100 && `(${item.porcentaje}%)`}</span>
                        <span>${item.monto?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {myOrders.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <Receipt className="w-10 h-10 text-gray-300" />
                </div>
                <h3 className="font-semibold text-gray-700 mb-2">Sin pedidos aún</h3>
                <p className="text-gray-500 text-sm mb-6">
                  Los pedidos que hagas aparecerán aquí
                </p>
                <Button onClick={() => setActiveTab('menu')} className="bg-brand hover:bg-brand/90 rounded-full px-6">
                  Ver Menú
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Orders list */}
                {myOrders.map((order) => (
                  <div key={order.order_id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-medium text-gray-900">
                          Pedido #{order.order_id?.slice(-6)}
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(order.created_at).toLocaleTimeString('es', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </p>
                      </div>
                      <Badge className={`rounded-full px-3 ${getOrderStatusColor(order.status)}`}>
                        {order.status === 'Listo' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                        {order.status}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2">
                      {order.items?.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-gray-700">
                            {item.quantity}x {item.product_name}
                            {item.variant && <span className="text-gray-400"> ({item.variant})</span>}
                          </span>
                          <span className="font-medium text-gray-900">${item.subtotal?.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between font-semibold">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="text-gray-900">${order.subtotal?.toFixed(2)}</span>
                    </div>
                  </div>
                ))}

                {/* Total summary card */}
                <div className="bg-gradient-to-br from-brand to-brand/90 text-brand-foreground rounded-2xl p-5 shadow-lg">
                  <div className="space-y-3">
                    <div className="flex justify-between text-white/80">
                      <span>Subtotal consumo</span>
                      <span className="font-mono">${calculateMyTotal().toFixed(2)}</span>
                    </div>
                    {session?.tax > 0 && (
                      <div className="flex justify-between text-sm text-white/60">
                        <span>Impuestos</span>
                        <span className="font-mono">Incluidos</span>
                      </div>
                    )}
                    <div className="flex justify-between text-2xl font-bold pt-3 border-t border-white/20">
                      <span>Total</span>
                      <span className="font-mono">${calculateMyTotal().toFixed(2)}</span>
                    </div>
                  </div>
                  
                  <p className="text-sm mt-5 text-white/70 text-center">
                    💳 Solicita la cuenta al mozo para pagar
                  </p>
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Check-in Dialog */}
      <Dialog open={showCheckInDialog} onOpenChange={setShowCheckInDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-brand" />
              Registro en la Mesa
            </DialogTitle>
            <DialogDescription>
              Ingresa tu nombre para registrar tus pedidos por separado
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Input
                placeholder="Tu nombre"
                value={participantName}
                onChange={(e) => setParticipantName(e.target.value)}
                className="text-lg"
                autoFocus
              />
            </div>
            
            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
              <p>💡 Esto te permite:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Ver solo tus pedidos</li>
                <li>Dividir la cuenta fácilmente</li>
                <li>Hacer seguimiento de tu consumo</li>
              </ul>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setShowCheckInDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCheckIn} className="bg-brand">
              <UserPlus className="w-4 h-4 mr-2" />
              Registrarme
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Configuration Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          {selectedProduct && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedProduct.name}</DialogTitle>
                {selectedProduct.description && (
                  <DialogDescription>{selectedProduct.description}</DialogDescription>
                )}
              </DialogHeader>

              <div className="space-y-4">
                {/* Variants */}
                {selectedProduct.variants?.length > 0 && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">Variante</label>
                    <div className="flex flex-wrap gap-2">
                      {selectedProduct.variants.map((variant) => (
                        <button
                          key={variant.name}
                          className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                            productConfig.variant === variant.name
                              ? 'border-brand bg-brand/10'
                              : 'border-gray-200'
                          }`}
                          onClick={() => setProductConfig({ ...productConfig, variant: variant.name })}
                        >
                          {variant.name}
                          {variant.price_modifier > 0 && (
                            <span className="text-gray-500 ml-1">+${variant.price_modifier}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Extras */}
                {selectedProduct.extras?.length > 0 && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">Extras</label>
                    <div className="flex flex-wrap gap-2">
                      {selectedProduct.extras.map((extra) => (
                        <button
                          key={extra.name}
                          className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                            productConfig.extras.includes(extra.name)
                              ? 'border-brand bg-brand/10'
                              : 'border-gray-200'
                          }`}
                          onClick={() => {
                            const newExtras = productConfig.extras.includes(extra.name)
                              ? productConfig.extras.filter(e => e !== extra.name)
                              : [...productConfig.extras, extra.name];
                            setProductConfig({ ...productConfig, extras: newExtras });
                          }}
                        >
                          {extra.name}
                          {extra.price > 0 && <span className="text-gray-500 ml-1">+${extra.price}</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Notas / Instrucciones</label>
                  <Textarea
                    placeholder="Sin cebolla, bien cocido, etc."
                    value={productConfig.notes}
                    onChange={(e) => setProductConfig({ ...productConfig, notes: e.target.value })}
                  />
                </div>

                {/* Quantity */}
                <div className="flex items-center justify-between">
                  <span className="font-medium">Cantidad</span>
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setProductConfig({ 
                        ...productConfig, 
                        quantity: Math.max(1, productConfig.quantity - 1) 
                      })}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="text-xl font-bold w-8 text-center">{productConfig.quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setProductConfig({ 
                        ...productConfig, 
                        quantity: productConfig.quantity + 1 
                      })}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button onClick={handleAddToCart} className="w-full bg-brand">
                  Agregar ${(calculateItemPrice({ ...productConfig, product: selectedProduct })).toFixed(2)}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Cart Sheet */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="sm:max-w-md max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <ShoppingCart className="w-5 h-5 text-brand" />
              Tu Pedido
            </DialogTitle>
          </DialogHeader>

          {cart.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <ShoppingCart className="w-8 h-8 text-gray-300" />
              </div>
              <p className="text-gray-500">Tu carrito está vacío</p>
              <Button 
                variant="outline" 
                className="mt-4 rounded-full"
                onClick={() => setShowCart(false)}
              >
                Ver Menú
              </Button>
            </div>
          ) : (
            <>
              <ScrollArea className="max-h-[45vh]">
                <div className="space-y-3">
                  {cart.map((item, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <span className="font-semibold text-gray-900">{item.quantity}x {item.product.name}</span>
                          <span className="font-bold text-brand">${calculateItemPrice(item).toFixed(2)}</span>
                        </div>
                        {item.variant && (
                          <p className="text-sm text-gray-500">{item.variant}</p>
                        )}
                        {item.extras.length > 0 && (
                          <p className="text-sm text-gray-500">+ {item.extras.join(', ')}</p>
                        )}
                        {item.notes && (
                          <p className="text-sm text-orange-500 italic">📝 {item.notes}</p>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="text-red-500 hover:bg-red-50 rounded-full h-8 w-8"
                        onClick={() => handleRemoveFromCart(index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              <div className="pt-4 border-t space-y-4">
                <div className="flex justify-between text-xl font-bold">
                  <span>Total</span>
                  <span className="text-brand">${calculateTotal().toFixed(2)}</span>
                </div>

                {isIndividualMode && !participantId && (
                  <div className="p-3 bg-amber-50 rounded-xl text-sm text-amber-700 border border-amber-200">
                    <p>⚠️ Debes registrarte para hacer tu pedido individual</p>
                  </div>
                )}

                <Button 
                  onClick={handleSubmitOrder} 
                  disabled={submitting}
                  className="w-full h-14 bg-brand hover:bg-brand/90 rounded-xl text-lg font-semibold shadow-lg shadow-brand/20"
                  data-testid="submit-order-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Enviar Pedido
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Join Split Bill Dialog */}
      <Dialog open={showJoinSplitDialog} onOpenChange={setShowJoinSplitDialog}>
        <DialogContent className="max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-500" />
              Unirme a la cuenta
            </DialogTitle>
            <DialogDescription>
              Registrate para que te asignen tu parte del consumo
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Tu nombre *</label>
              <Input
                placeholder="Ej: Juan"
                value={joinName}
                onChange={(e) => setJoinName(e.target.value)}
                className="h-12 rounded-xl"
                data-testid="join-split-name"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Teléfono (opcional)</label>
              <Input
                placeholder="Ej: 1155551234"
                value={joinPhone}
                onChange={(e) => setJoinPhone(e.target.value)}
                className="h-12 rounded-xl"
                data-testid="join-split-phone"
              />
              <p className="text-xs text-muted-foreground">
                Para recibir tu cuenta por WhatsApp
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowJoinSplitDialog(false)}
              className="rounded-xl"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleJoinAsComensal}
              disabled={joiningAsComensal || !joinName.trim()}
              className="bg-violet-600 hover:bg-violet-700 rounded-xl"
              data-testid="confirm-join-split"
            >
              {joiningAsComensal ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2" />
              )}
              Unirme
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
